/**
 * Rune font loader (M0-4)
 *
 * Self-contained, side-effecting module. Importing this file kicks off a
 * one-shot check against `document.fonts.ready` to verify the bespoke rune
 * font ('RuneFont', declared via @font-face in `styles/tokens.css`) actually
 * resolved. If it did not — which is expected today, because the WOFF2 file
 * is a placeholder until C-2 lands — we emit a single informational
 * `console.warn`. The app is *not* blocked: `.rune` glyphs fall through the
 * Unicode-capable system-font stack defined by `--font-rune`.
 *
 * Not yet wired into `src/main.ts`. M0-6 (app shell + hash router) owns the
 * boot sequence and will import this module from there. Until then it can be
 * imported manually for ad-hoc testing.
 *
 * Refs: GitHub issue #4 (M0-4).
 */

const RUNE_FONT_FAMILY = 'RuneFont';

/**
 * Probe whether a named font family is actually available to the document.
 *
 * `document.fonts.check()` returns `true` if the given font would be used
 * for the given text. We probe with a representative Elder Futhark codepoint
 * (ᚠ, U+16A0) so the check is meaningful given our `unicode-range` scoping.
 */
function isRuneFontLoaded(): boolean {
  if (typeof document === 'undefined' || !document.fonts) {
    return false;
  }
  try {
    // Size + family string is the standard CSS font shorthand the API expects.
    return document.fonts.check(`16px "${RUNE_FONT_FAMILY}"`, 'ᚠ');
  } catch {
    // Some test environments throw on .check(); treat as "not loaded".
    return false;
  }
}

/**
 * Wait for the browser's font loading pipeline to settle, then warn if the
 * rune font is still missing. Returns the underlying promise so callers
 * (and tests) can await completion.
 */
export function verifyRuneFont(): Promise<void> {
  if (typeof document === 'undefined' || !document.fonts || !document.fonts.ready) {
    return Promise.resolve();
  }

  return document.fonts.ready.then(() => {
    if (!isRuneFontLoaded()) {
      // Informational only — the Unicode fallback stack handles rendering.
      console.warn(
        `[font-loader] Rune font "${RUNE_FONT_FAMILY}" did not load. ` +
          'Falling back to system fonts with Runic coverage ' +
          '(see --font-rune in tokens.css). This is expected until the bespoke ' +
          'WOFF2 ships via issue #C-2.',
      );
    }
  });
}

// Side effect on import: kick off the verification without blocking.
// We intentionally do not await — boot should never wait on font checks.
void verifyRuneFont();
